// V1
import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, catchError, firstValueFrom, map } from 'rxjs';
import { User, UserSettings, defaultUser } from 'src/app/interfaces/api/user';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { environment } from '../../../environments/environment';
import { LocalStorageService } from './local-storage.service';

interface SessionResponse {
  accessToken: string;
  expiresAt: string;
  user: User;
}

/**
 * The BFF (auth-api) lives behind /bff on the app's own host: the Angular service worker must never
 * handle it, or it may hold the request for minutes (seen in prod on login and logout).
 * `ngsw-bypass` (header for XHR, query parameter for a page navigation) makes the SW step aside.
 */
const NGSW_BYPASS = { 'ngsw-bypass': 'true' };

function bypassServiceWorker(url: string): string {
  if (!url.startsWith('/bff/')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}ngsw-bypass=true`;
}

/** GET /bff/session (auth-api BFF, same origin as the app). */
interface BffSession {
  accessToken: string;
  expiresIn: number;
  user: { sub: string; email: string; name?: string };
}

/** Marks a request the interceptor must not retry after a refresh (avoids refresh loops). */
export const SKIP_AUTH_RETRY = new HttpContextToken<boolean>(() => false);

// More redirects to the login than this in a minute = a loop: stop and say so.
const LOGIN_LOOP_LIMIT = 3;
const LOGIN_ATTEMPTS_KEY = 'uf_login_attempts';

export type LoginResult =
  | { type: 'success'; token: string }
  | { type: 'mfa_required'; mfaToken: string; mfaMethod: string; expiresAt: string };

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Services
  private http = inject(HttpClient);
  private router = inject(Router);
  private localStorage = inject(LocalStorageService);
  private projectService = inject(ProjectService);

  authBase = environment.AUTH_API;
  private accessToken = signal<string | null>(null);
  private expiresAt = signal<Date | null>(null);
  private user = signal<User | null>(null);
  private static readonly REFRESH_BUFFER_MS = 60_000;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  /** Refonte auth: the session lives in auth-api's BFF (httpOnly cookie), the token in memory only. */
  readonly oidc = environment.OIDC;
  private sessionSub: string | null = null;
  private sessionRequest: Promise<boolean> | null = null;
  /** A full-page navigation to the BFF has started: the app is going away. */
  private leaving = false;

  readonly isAuthenticated = computed(() => {
    const token = this.accessToken();
    const exp = this.expiresAt();
    return Boolean(token && exp && exp.getTime() > Date.now());
  });

  // Variables
  private readonly API_URL = environment.API_URL;

  // Signals
  private readonly _currentUser = signal<User>(defaultUser);
  readonly currentUser = this._currentUser.asReadonly();

  // Observable
  readonly currentUserObs = toObservable(this.currentUser);

  // Computed
  readonly isPrivileged = computed(() => {
    const role = this._currentUser().role?.name;
    return role === 'admin' || role === 'dev';
  });

  readonly canManageProject = computed(() => {
    const role = this._currentUser().role?.name;
    return role === 'userAdmin' || role === 'admin' || role === 'dev';
  });

  private readonly _projectsVisited = computed(() => this._currentUser().projectsVisited ?? []);
  readonly projectsVisited = this._projectsVisited;

  constructor() {
    const user: User = this.localStorage.getItem('user') as User;

    if (user) {
      this._currentUser.set(user);
    }

    if (this.oidc) {
      // Back from sleep or another tab: timers may have been frozen, check the token now.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.accessToken()) this.ensureFreshToken();
      });
    }

    this.currentUserObs = toObservable(this.currentUser);
  }

  //---GET---//
  public get currentUserValue(): User {
    return this.currentUser();
  }

  // Method to retrieve token
  getDb(): string | null {
    let db: any = this.localStorage.getItem('db')
    return db;
  }

  // Method to retrieve token
  getToken(): string | null {
    return this.localStorage.getItem('authToken');
  }

  getEmail(): string | null {
    return this.localStorage.getItem('email');
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    return !!token;
  }

  forgotPassword(email: string): Observable<void> {
    return this.http
      .post<void>(`${this.API_URL}/forgetPassword`, { email })
      .pipe(
        catchError((error) => {
          console.error(
            'Erreur lors de la demande de réinitialisation :',
            error
          );
          throw new Error("Une erreur s'est produite. Vérifiez votre email.");
        })
      );
  }

  // Public route (see unifield-api-v3's middleware/public.js) - reached from the "Besoin de
  // nous contacter ?" form on the login screen, before any session exists. Mirrors
  // unifield-mobile-2's own AuthService.contactUs() exactly (same backend endpoint/body shape).
  contactUs(data: { name: string; email: string; object: string; message: string }): Observable<any> {
    return this.http.post(`${this.API_URL}/contact-us`, data);
  }

  //---POST---//

  login(credentials: { email: string; password: string; remember?: boolean }): Observable<LoginResult> {
    this.localStorage.clear();

    return this.http.post<any>(`${this.API_URL}/login/app`, credentials).pipe(
      map((response) => {
        if (response.mfaRequired) {
          if (credentials.email)
            this.localStorage.setItem('email', credentials.email);

          return {
            type: 'mfa_required' as const,
            mfaToken: response.mfaToken,
            mfaMethod: response.mfaMethod,
            expiresAt: response.expiresAt,
          };
        }

        if (!response.accessToken)
          throw new Error('No access token');

        this._currentUser.set(response.user);
        this.localStorage.setItem('user', response.user);
        this.saveToken(response.accessToken, credentials?.email);
        return { type: 'success' as const, token: response.accessToken };
      }),

      catchError((error) => {
        if (error.error?.error === 'method_not_allowed')
          throw new Error('method_not_allowed');

        throw new Error('Erreur de connexion, vérifiez vos identifiants');
      })
    );
  }

  verifyMfa(mfaToken: string, code: string): Observable<void> {
    return this.http.post<any>(`${this.API_URL}/account/mfa/verify`, { mfaToken, code }).pipe(
      map((response) => {
        if (!response.accessToken)
          throw new Error('No access token');

        this._currentUser.set(response.user);
        this.localStorage.setItem('user', response.user);
        this.saveToken(response.accessToken);
      }),
      catchError((error) => {
        if (error.error?.error === 'invalid_code')
          throw new Error('invalid_code');

        throw new Error('Erreur de vérification du code');
      })
    );
  }

  // Method to save token in localStorage
  saveToken(token: string, email?: string): void {
    this.localStorage.setItem('authToken', token);
    if (email) this.localStorage.setItem('email', email);
  }

  // Method to save token in localStorage
  setProjectKey(project: string, accessKey: string): void {
    this.localStorage.setItem('project', project);
    this.localStorage.setItem('accessKey', accessKey);
  }

  saveWithAccessToken(
    accessToken: string,
    refreshToken: string,
    tokenID: string,
    csrfToken: string
  ): void {
    this.localStorage.setItem('access_token', accessToken);
    this.localStorage.setItem('refresh_Token', refreshToken);
    this.localStorage.setItem('token_id, tokenID', tokenID);
    this.localStorage.setItem('csrf_Token', csrfToken);
  }


  //---UPDATE USER---//

  setUser(user: User) {
    this._currentUser.set(user);
    this.localStorage.setItem('user', user);
    this.saveToken(user.accessToken as string, user.email);
  }

  updateProjectsVisited(projects: string[]) {
    const user = { ...this._currentUser(), projectsVisited: projects };
    this._currentUser.set(user);
    this.localStorage.setItem('user', user);
  }

  updateFavouriteProjects(favourites: string[]) {
    const user = { ...this._currentUser(), favouriteProjects: favourites };
    this._currentUser.set(user);
    this.localStorage.setItem('user', user);
  }

  updateSettings(settings: UserSettings) {
    const user = { ...this._currentUser(), settings };
    this._currentUser.set(user);
    this.localStorage.setItem('user', user);
  }

  resetPassword(data: any) {
    // Not '/auth/resetPassword' - that path doesn't exist and falls through to the
    // session-auth middleware chain, which 401s it before ever reaching the handler.
    // The real route is unauthenticated (a user resetting a forgotten password has no
    // session yet) and lives at the repo root: routes/public.js -> PUT /resetPassword.
    return this.http.put(this.API_URL + '/resetPassword', data);
  }

  getAccessToken(): string | null {
    return this.accessToken();
  }

  // async login2(email: string, password: string, rememberMe = false): Promise<void> {
  //   const body = await firstValueFrom(
  //     this.http.post<LoginResponse>(
  //       `${this.authBase}/v1/auth/login`,
  //       { email, password, rememberMe },
  //       { withCredentials: true }, // OBLIGATOIRE : reçoit le cookie refresh
  //     ),
  //   );
  //   this.applyLoginResponse(body);
  // }

  loginWithMicrosoft(returnPath: string = '/auth/callback'): void {
    const url = new URL(`${environment.AUTH_API}/v1/auth/microsoft/start`);
    url.searchParams.set('consumer', environment.AUTH_CONSUMER);
    url.searchParams.set('returnTo', `${window.location.origin}${returnPath}`);
    window.location.href = url.toString();
  }

  loginWithGoogle(returnPath: string = '/auth/callback'): void {
    const url = new URL(`${environment.AUTH_API}/v1/auth/google/start`);
    url.searchParams.set('consumer', environment.AUTH_CONSUMER);
    url.searchParams.set('returnTo', `${window.location.origin}${returnPath}`);
    window.location.href = url.toString();
  }

  async completeSsoLogin(authCode: string): Promise<void> {
    try {
      const body = await firstValueFrom(
        this.http.post<SessionResponse>(
          `${environment.API_URL}/auth/complete`,
          { authCode },
          { withCredentials: true },
        ),
      );

      this.apply(body);
    } catch (err: any) {
      const apiError = err?.error?.error ?? 'exchange_failed';
      throw new Error(apiError);
    }
  }

  async bootstrap(): Promise<void> {
    if (this.oidc) {
      // Tokens of the old login must not be used next to the OIDC session.
      this.localStorage.removeItem('authToken');
      this.localStorage.removeItem('email');
      await this.refresh();
      return;
    }

    // Session héritée (email/password) : le token vit dans le localStorage et
    // n'a pas de cookie SSO. On ne tente pas /auth/me pour ne pas l'écraser.
    if (this.isLoggedIn()) {
      return;
    }

    for (let attempt = 0; ; attempt++) {
      try {
        const body = await firstValueFrom(
          this.http.get<SessionResponse>(`${environment.API_URL}/auth/me`, {
            withCredentials: true,
          }),
        );
        this.apply(body);
        return;
      } catch (err: any) {
        const delay = AuthService.REFRESH_RETRY_DELAYS_MS[attempt];
        if (err?.status === 503 && delay !== undefined) {
          console.warn(`[AuthService] bootstrap transient failure (attempt ${attempt + 1}), retrying in ${delay}ms`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        // Pas de session SSO : on réinitialise uniquement l'état SSO en mémoire,
        // sans purger une éventuelle session héritée du localStorage.
        this.accessToken.set(null);
        this.expiresAt.set(null);
        return;
      }
    }
  }

  // A 503 (`retryable: true`) means the session itself is still fine server-side
  // and only this attempt hit a transient hiccup (network blip, auth-api
  // momentarily down) — worth a couple of short retries before giving up. A
  // 401 means the session is genuinely gone; no point retrying that.
  private static readonly REFRESH_RETRY_DELAYS_MS = [2000, 4000];

  async refresh(): Promise<boolean> {
    if (this.oidc) {
      // One call at a time, however many requests got a 401 together.
      this.sessionRequest ??= this.loadBffSession().finally(() => (this.sessionRequest = null));
      return this.sessionRequest;
    }

    for (let attempt = 0; ; attempt++) {
      try {
        const body = await firstValueFrom(
          this.http.post<SessionResponse>(
            `${environment.API_URL}/auth/refresh`,
            {},
            { withCredentials: true },
          ),
        );
        this.apply(body);
        return true;
      } catch (err: any) {
        const delay = AuthService.REFRESH_RETRY_DELAYS_MS[attempt];
        if (err?.status === 503 && delay !== undefined) {
          console.warn(`[AuthService] refresh transient failure (attempt ${attempt + 1}), retrying in ${delay}ms`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        console.warn('[AuthService] refresh failed, clearing session', err?.status);
        this.clear();
        return false;
      }
    }
  }

  async logout(): Promise<void> {
    if (this.oidc) {
      await this.endBffSession();
      this.clear();
      // Straight to the auth-api login page, no stop in the app.
      this.startLogin('/');
      return;
    }

    try {
      await firstValueFrom(
        this.http.post(`${environment.API_URL}/auth/logout`, {}, { withCredentials: true }),
      );
    } catch {
      // ignore — on nettoie localement de toute façon
    }
    this.clear();
    this.router.navigate(['/auth']);
  }

  /** Signed out: OIDC goes straight to the auth-api login page, the old login to /auth. */
  requireLogin(returnUrl = '/'): void {
    if (this.oidc) this.startLogin(returnUrl);
    else this.router.navigate(['/auth'], { queryParams: { returnUrl } });
  }

  /** OIDC: sends the browser to the auth-api login page, through the BFF. */
  startLogin(returnUrl = '/'): boolean {
    if (this.leaving) return false;
    
    const now = Date.now();
    const recent = this.readAttempts().filter((t) => now - t < 60_000);
    const target = returnUrl.startsWith('/') ? returnUrl : '/';

    if (recent.length >= LOGIN_LOOP_LIMIT) {
      console.error('[AuthService] login loop stopped: auth-api signs in, but the session is refused right after', recent);
      this.showLoginError('login_failed');
      return false;
    }

    sessionStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify([...recent, now]));
    this.navigate(`/bff/login?returnTo=${encodeURIComponent(target)}`);
    return true;
  }

  /** The login error page lives in auth-api (BFF), in the app's look. */
  private showLoginError(reason: 'login_failed' | 'no_local_account'): void {
    // The user starts over from that page: earlier attempts no longer count as a loop.
    sessionStorage.removeItem(LOGIN_ATTEMPTS_KEY);
    this.navigate(`/bff/error?reason=${reason}`);
  }

  // GET /bff/session gives a valid access token (the BFF renews it) and slides the session.
  // 401 = signed out. 503 = auth-api hiccup: retry, and never sign the user out for it.
  private async loadBffSession(): Promise<boolean> {
    for (let attempt = 0; ; attempt++) {
      try {
        const body = await firstValueFrom(this.http.get<BffSession>('/bff/session', { headers: NGSW_BYPASS }));
        const expiresAt = new Date(Date.now() + body.expiresIn * 1000);

        this.accessToken.set(body.accessToken);
        this.expiresAt.set(expiresAt);
        this.scheduleProactiveRefresh(expiresAt);

        if (this.sessionSub !== body.user.sub) {
          const loaded = await this.loadUnifieldUser();
          if (loaded === 'no_account') return false;
          if (loaded === 'ok') this.sessionSub = body.user.sub;
        }

        sessionStorage.removeItem(LOGIN_ATTEMPTS_KEY);
        return true;
      } catch (err: any) {
        const delay = AuthService.REFRESH_RETRY_DELAYS_MS[attempt];

        if (err?.status === 503 && delay !== undefined) {
          console.warn(`[AuthService] /bff/session unavailable (attempt ${attempt + 1}), retrying in ${delay}ms`, err?.error);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        if (err?.status === 401) {
          console.info('[AuthService] no BFF session', err?.error?.reason ?? '');
          this.clear();
          return false;
        }

        // Still failing: keep what we have, the session is most likely alive.
        console.error('[AuthService] /bff/session failed, keeping the current state', err?.status, err?.error);
        return Boolean(this.accessToken());
      }
    }
  }

  /** The full Unifield user (role, projects…) from unifield-api, for the signed-in account. */
  private async loadUnifieldUser(): Promise<'ok' | 'failed' | 'no_account'> {
    try {
      const user = await firstValueFrom(
        this.http.get<User>(`${this.API_URL}/users/me`, {
          // Called from inside refresh(): a retry would wait on itself.
          context: new HttpContext().set(SKIP_AUTH_RETRY, true),
        }),
      );
      this._currentUser.set(user);
      this.localStorage.setItem('user', user);
      return 'ok';
    } catch (err: any) {
      if (err?.status === 403 && err?.error?.error === 'no_local_account') {
        console.error('[AuthService] signed in to auth-api, but no Unifield account matches it', err.error);
        // Ends the auth-api login too, so another account can be used.
        await this.endBffSession();
        this.clear();
        this.showLoginError('no_local_account');
        return 'no_account';
      }

      // The session is fine; only the profile failed. Keep the cached one, retry on the next refresh.
      console.error('[AuthService] cannot load the Unifield user', err?.status, err?.error);
      return 'failed';
    }
  }

  /** Ends the BFF session and the auth-api login. Returns where to go next. */
  private async endBffSession(): Promise<string> {
    try {
      // The custom header is the BFF's CSRF guard.
      const body = await firstValueFrom(
        this.http.post<{ redirectTo: string }>('/bff/logout', {}, { headers: { 'X-UF-BFF': '1', ...NGSW_BYPASS } }),
      );
      return body.redirectTo;
    } catch (err: any) {
      console.error('[AuthService] BFF logout failed', err?.status, err?.error);
      return '/';
    }
  }

  private readAttempts(): number[] {
    try {
      return JSON.parse(sessionStorage.getItem(LOGIN_ATTEMPTS_KEY) ?? '[]');
    } catch {
      return [];
    }
  }

  // Full page navigation (the BFF and auth-api live outside the Angular router).
  // The first one wins: the guard or interceptor reacting to the same sign-out
  // must not replace the error page with the login page.
  private navigate(url: string): void {
    if (this.leaving) return;
    this.leaving = true;
    this.leavePage(url);
  }

  private leavePage(url: string): void {
    window.location.assign(bypassServiceWorker(url));
  }

  private apply(body: SessionResponse): void {
    console.log("Applying session response in AuthService:", body);
    this.accessToken.set(body.accessToken);
    const expiresAt = new Date(body.expiresAt);
    this.expiresAt.set(expiresAt);
    this.scheduleProactiveRefresh(expiresAt);
    // Le token SSO est opaque (pas un JWT) : on ne l'écrit pas dans
    // localStorage('authToken'), qui est réservé au JWT du login legacy et
    // lu par isLoggedIn()/getToken() ainsi que par l'intercepteur HTTP.

    this.http.post<any>(this.API_URL + '/users/sync', { email: body.user.email, }, { withCredentials: true }).subscribe((result) => {
      if (result && result.user) {
        console.log("User sync result:", result);
        const user: User = result.user;
        this._currentUser.set(user);
        // Cohérence avec le flux email/password : on persiste l'utilisateur
        // pour que le code lisant localStorage('user') voie aussi la session SSO.
        this.localStorage.setItem('user', user);
        console.log("User synced:", user);
      }
    });
  }

  private clear(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    this.accessToken.set(null);
    this.expiresAt.set(null);
    this.sessionSub = null;
    this._currentUser.set({ ...defaultUser });
    // Purge les jetons hérités du localStorage pour que isLoggedIn() reflète
    // l'état réel de la session : sinon le guard laisse passer malgré le 401.
    this.localStorage.removeItem('authToken');
    this.localStorage.removeItem('email');
    this.localStorage.removeItem('user');
  }

  // Silent refresh: schedule the next /auth/refresh call shortly before the current access
  // token actually expires, so isAuthenticated() never goes false while the underlying
  // refresh-token session (14 days, up to 90 with "remember me") is still valid. Without
  // this, any navigation more than ~15 minutes after the last refresh was bouncing SSO
  // users back to /auth even though their session was still perfectly alive server-side.
  private scheduleProactiveRefresh(expiresAt: Date): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const delay = expiresAt.getTime() - Date.now() - AuthService.REFRESH_BUFFER_MS;

    this.refreshTimer = setTimeout(() => {
      if (this.oidc) this.ensureFreshToken();
      else void this.refresh();
    }, Math.max(delay, 0));
  }

  // OIDC: renew the token if it is about to expire. Session lost in the background:
  // go through the login right away (instant with SSO) and come back to this page.
  private ensureFreshToken(): void {
    const exp = this.expiresAt();
    if (exp && exp.getTime() - Date.now() > AuthService.REFRESH_BUFFER_MS) return;

    void this.refresh().then((ok) => {
      if (ok) return;
      console.warn('[AuthService] session lost in the background, signing in again');
      this.requireLogin(this.router.url);
    });
  }
}
